"use client"

import { useState, useEffect, useCallback, useRef } from "react"
import { useSocket } from "@/hooks/use-socket"
import { addEventListener, joinOrderChannel, leaveOrderChannel } from "@/lib/socket"
import { checkOrderStatus, type OrderStatus } from "@/lib/api"

export function useOrderWebSocket(orderId: string | null, secaoId?: string, estabelecimentoId?: string) {
  // debounce map: chave = orderId, valor = timer
  const debouncedUpdates = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  const [orderStatus, setOrderStatus] = useState<OrderStatus | null>(null)
  const [isListening, setIsListening] = useState(false)
  const [lastFetchTime, setLastFetchTime] = useState<number>(0)
  const { isConnected, socket } = useSocket()

  // Guardas para evitar fetchs repetidos / concorrentes
  const inFlightRef = useRef(false)
  const mountedRef = useRef(true)
  const minFetchIntervalMs = 5000 // 5s - evita flood de requests

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      // limpando timers pendentes no unmount por segurança
      debouncedUpdates.current.forEach((timer) => clearTimeout(timer))
      debouncedUpdates.current.clear()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Função para processar atualizações do pedido específico (com debounce)
  const handleOrderUpdate = useCallback(
    (updatedOrder: OrderStatus) => {
      if (!orderId) return null

      // Verificar se é o pedido correto
      if (updatedOrder._id !== orderId && updatedOrder.id !== orderId) {
        return null
      }

      // Cancelar atualização anterior pendente para este pedido
      const key = updatedOrder._id ?? updatedOrder.id
      if (!key) return null

      const existingTimer = debouncedUpdates.current.get(key)
      if (existingTimer) {
        clearTimeout(existingTimer)
      }

      // Agendar nova atualização com debounce (300ms)
      const timer = setTimeout(() => {
        // evita setState com mesmo objeto/mesmo status para reduzir rerenders
        setOrderStatus((prev) => {
          if (prev && prev._id === updatedOrder._id && prev.status === updatedOrder.status) {
            return prev
          }
          return updatedOrder
        })
        setLastFetchTime(Date.now())

        // Limpar o timer após execução
        debouncedUpdates.current.delete(key)
      }, 300)

      debouncedUpdates.current.set(key, timer)

      return updatedOrder
    },
    [orderId],
  )

  // Função para buscar status atual do pedido via API (com dedup)
  const fetchCurrentStatus = useCallback(async (): Promise<OrderStatus | null> => {
    if (!orderId) {
      console.log(`[useOrderWebSocket] 🔍 fetchCurrentStatus abortado: orderId não definido`)
      return null
    }

    // throttle: não buscar se já buscamos recentemente
    const now = Date.now()
    if (now - lastFetchTime < minFetchIntervalMs) {
      console.log(
        `[useOrderWebSocket] ⏱ Ignorando fetch (throttle). ${Math.round((now - lastFetchTime) / 1000)}s desde último.`,
      )
      return orderStatus
    }

    // evita fetch concorrente
    if (inFlightRef.current) {
      console.log(`[useOrderWebSocket] ⏳ Fetch já em andamento, ignorando nova chamada.`)
      return orderStatus
    }

    inFlightRef.current = true
    try {
      console.log(`[useOrderWebSocket] 🔍 Buscando status atual do pedido ${orderId} via API...`)
      const status = await checkOrderStatus(orderId)
      if (!mountedRef.current) return null

      console.log(`[useOrderWebSocket] ✅ Status atual obtido via API:`, status)
      setOrderStatus(status)
      setLastFetchTime(Date.now())
      return status
    } catch (error) {
      console.error(`[useOrderWebSocket] ❌ Erro ao buscar status via API:`, error)
      return null
    } finally {
      inFlightRef.current = false
    }
  }, [orderId, lastFetchTime, orderStatus])

  // Quando orderId for removido/alterado para null, limpar estado interno e timers
  useEffect(() => {
    if (!orderId) {
      setOrderStatus(null)
      setLastFetchTime(0)
      inFlightRef.current = false
      // limpar timers pendentes relativos a pedidos anteriores
      debouncedUpdates.current.forEach((timer) => clearTimeout(timer))
      debouncedUpdates.current.clear()
    }
  }, [orderId])

  // Configurar listeners quando conectado e com orderId
  useEffect(() => {
    if (!socket || !orderId) {
      console.log(`[useOrderWebSocket] ⚠️ Não pode configurar listeners:`, {
        isConnected,
        hasSocket: !!socket,
        orderId,
      })
      setIsListening(false)
      return
    }

    if (!isConnected) {
      console.log(`[useOrderWebSocket] ⚠️ Socket não conectado ainda - aguardando conexão para ${orderId}`)
      return
    }

    console.log(`[useOrderWebSocket] 🔌 Configurando listeners para pedido ${orderId}`)
    setIsListening(true)

    // juntar canal
    joinOrderChannel(orderId, secaoId, estabelecimentoId)

    // listener principal vindo de addEventListener (se existir)
    const removeMainListener = addEventListener("atualizarPedidoId", handleOrderUpdate)

    // listeners para eventos solicitados pelo backend
    const events = [
      "atualizarPedidoId",
      "atualizarPedido",
      "atualizarPedidoSecao",
      `atualizarPedido_${orderId}`,
      "orderUpdate",
      "pedidoAtualizado",
      "statusChanged",
      "orderStatusUpdated",
    ]

    // directListener referência estável
    const directListener = (data: any, eventName?: string) => {
      console.log(`[useOrderWebSocket] 🔔 Mensagem recebida via evento '${eventName || "desconhecido"}':`, data)
      if (!data) return

      let orderData = data
      // Tentar encontrar o pedido em diferentes campos comuns
      if (data.order) orderData = data.order
      if (data.pedido) orderData = data.pedido
      if (data.data) orderData = data.data
      if (data.payload) orderData = data.payload

      if (orderData && (orderData._id === orderId || orderData.id === orderId)) {
        console.log(`[useOrderWebSocket] ✅ Atualização válida para o pedido atual (${orderId}):`, orderData.status)
        handleOrderUpdate(orderData)
      } else {
        console.log(`[useOrderWebSocket] ℹ️ Atualização ignorada (ID diferente ou inválido):`, orderData?._id || orderData?.id)
      }
    }

    // configurar listeners individuais com logs
    events.forEach((eventName) => {
      socket.off(eventName) // limpar antes
      socket.on(eventName, (data: any) => directListener(data, eventName))
      console.log(`[useOrderWebSocket] 👂 Ouvindo evento: ${eventName}`)
    })

    // onAny: capturar possíveis formatos - guardar a referência para limpar depois
    const anyHandler = (eventName: string, ...args: any[]) => {
      args.forEach((arg) => {
        if (arg && (arg._id === orderId || arg.id === orderId)) {
          handleOrderUpdate(arg)
        }
      })
    }

    // remover o anyHandler antigo (se existir) antes de adicionar
    if (socket.offAny) {
      try {
        socket.offAny(anyHandler)
      } catch (e) {
        // alguns clientes não aceitam offAny(fn) - ignorar se falhar
      }
    }
    if (socket.onAny) socket.onAny(anyHandler)

    // Se os dados estiverem antigos, buscar (respeita throttle/dedup)
    const timeSinceLastFetch = Date.now() - lastFetchTime
    if (timeSinceLastFetch > minFetchIntervalMs && !inFlightRef.current) {
      fetchCurrentStatus().catch(() => { })
    }

    return () => {
      // cleanup: leave channel, remover listeners e limpar timers pendentes
      leaveOrderChannel(orderId, secaoId, estabelecimentoId)
      try {
        removeMainListener()
      } catch (e) {
        // ignore
      }
      events.forEach((eventName) => {
        try {
          socket.off(eventName)
        } catch (e) {
          // ignore
        }
      })
      if (socket.offAny) {
        try {
          socket.offAny(anyHandler)
        } catch (e) {
          // ignore
        }
      }
      // limpar timers pendentes (por segurança)
      debouncedUpdates.current.forEach((timer) => clearTimeout(timer))
      debouncedUpdates.current.clear()

      setIsListening(false)
      console.log(`[useOrderWebSocket] 🧹 Cleanup concluído para pedido ${orderId}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, isConnected, orderId, secaoId, estabelecimentoId, handleOrderUpdate])

  const refreshStatus = useCallback(async () => {
    return await fetchCurrentStatus()
  }, [fetchCurrentStatus])

  const simulateUpdate = useCallback(
    (newStatus: string) => {
      if (!orderStatus) return
      const simulatedUpdate: OrderStatus = {
        ...orderStatus,
        status: newStatus as any,
        updatedAt: new Date().toISOString(),
      }
      console.log(`[useOrderWebSocket] 🧪 Simulando atualização:`, simulatedUpdate)
      handleOrderUpdate(simulatedUpdate)
    },
    [orderStatus, handleOrderUpdate],
  )

  return {
    orderStatus,
    isListening,
    isConnected,
    fetchCurrentStatus,
    refreshStatus,
    simulateUpdate,
    lastFetchTime,
  }
}
